import clsx from "clsx";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect } from "react";

import IconEnvelope from "../../assets/icons/icon-envelope.svg";
import IconPrinter from "../../assets/icons/icon-printer.svg";

import Modal from "../../components/modal";

import styles from "./product-details-modal.module.scss";

const optionFields = (option) => [
  {
    name: "Membership Type",
    field: "membershipType"
  },
  {
    name:
      option.optionType === "Event" ? "Ideal Event Size" : "Ideal Team Size",
    field: "roomSize"
  },
  {
    name: "Hours of Access",
    field: "hoursOfAccess"
  },
  {
    name: "Boardroom Credits",
    field: "boardroomCredits"
  },
  {
    name: "Guest Policy",
    field: "guestPolicy"
  },
  {
    name: "Price",
    field: "price",
    className: "text-link"
  }
];

const ProductDetailsModal = ({ option, onRequestClose, ...props }) => {
  const router = useRouter();

  const handlePrint = () => {
    window.print();
  };

  useEffect(() => {
    const handleRouteChangeStart = () => {
      onRequestClose();
    };

    router.events.on("routeChangeStart", handleRouteChangeStart);

    return () => {
      router.events.off("routeChangeStart", handleRouteChangeStart);
    };
  }, [router.events, onRequestClose]);

  return (
    <Modal onRequestClose={onRequestClose} {...props}>
      <div className={clsx("container", styles.ProductDetailsModal)}>
        <div className={styles.ProductWrapper}>
          <div className={styles.ProductDetails}>
            <h3 className={styles.ProductName}>{option.name}</h3>
            <div className={styles.ProductHeading}>
              <h2 className={styles.ProductDetailsHeading}>Product Details</h2>
              <div className={styles.ProductActions}>
                <IconPrinter
                  className="cursor-pointer"
                  width="26"
                  role="button"
                  onClick={handlePrint}
                />
                <Link
                  href={`mailto:?subject=${option.name}&body=${option.description}`}
                  target="_blank"
                  rel="noreferrer"
                  passHref
                >
                  <a>
                    <IconEnvelope width="26" />
                  </a>
                </Link>
              </div>
            </div>
            <p>{option.subtitle}</p>

            <div className={styles.ProductDataList}>
              {optionFields(option)
                .filter((optionField) => option[optionField.field])
                .map((optionField) => (
                  <div className={styles.ProductData} key={optionField.field}>
                    <label>{optionField.name}</label>
                    <p className={optionField.className}>
                      {option[optionField.field]}
                    </p>
                  </div>
                ))}
            </div>
          </div>
          <div className={styles.ProductImage}>
            {/* Mobile image */}
            <Image
              src={`${option.image?.url}?w=520&fm=webp&q=80`}
              width="517"
              height="517"
              layout="responsive"
              alt={option.image?.description}
            />
          </div>
        </div>

        {option.featuresCollection.items.length > 0 && (
          <div className={styles.FeatureList}>
            <h3>Features</h3>

            {option.featuresCollection.items.map((feature) => (
              <div key={feature.title} className={styles.FeatureItem}>
                <label>{feature.title}</label>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        )}

        {option.serviceAmenitiesCollection.items.length > 0 && (
          <div className={styles.FeatureList}>
            <h3>{option.serviceAmenitiesTitle || "Service Amenities"}</h3>

            {option.serviceAmenitiesCollection.items.map((feature) => (
              <div key={feature.title} className={styles.FeatureItem}>
                <label>{feature.title}</label>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        )}

        {option.spaceAmenitiesCollection.items.length > 0 && (
          <div className={styles.FeatureList}>
            <h3>{option.spaceAmenitiesTitle || "Space Amenities"}</h3>

            {option.spaceAmenitiesCollection.items.map((feature) => (
              <div key={feature.title} className={styles.FeatureItem}>
                <label>{feature.title}</label>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        )}

        {option.techAmenitiesCollection.items.length > 0 && (
          <div className={styles.FeatureList}>
            <h3>{option.techAmenitiesTitle || "Tech Amenities"}</h3>

            {option.techAmenitiesCollection.items.map((feature) => (
              <div key={feature.title} className={styles.FeatureItem}>
                <label>{feature.title}</label>
                <p>{feature.description}</p>
                {feature.price && (
                  <p className={styles.Link}>{feature.price}</p>
                )}
              </div>
            ))}
          </div>
        )}

        {option.upgradesCollection.items.length > 0 && (
          <div className={styles.FeatureList}>
            <h3>
              {option.upgradesTitle || "Upgrades (additional costs apply)"}
            </h3>

            {option.upgradesCollection.items.map((feature) => (
              <div key={feature.title} className={styles.FeatureItem}>
                <label>{feature.title}</label>
                {feature.description && <p>{feature.description}</p>}
                {feature.price && (
                  <p className={styles.Link}>{feature.price}</p>
                )}
                {feature.option && (
                  <Link
                    href={`/options/${feature.option.slug}`}
                    passHref
                    onClick={onRequestClose}
                  >
                    <p className={styles.Link}>View {feature.option.name}</p>
                  </Link>
                )}
              </div>
            ))}
          </div>
        )}

        <div className={styles.Footer}>
          <div>
            <h4>Want to get in touch?</h4>
            <p>
              Schedule a visit online,
              <br /> connect with us: info@eastroom.com <br />
              Or, call us at: (416) 628-5333
            </p>
          </div>
          <div className={styles.Logo}>
            <Image
              src="/images/logo.png"
              layout="fill"
              alt="East Room"
              objectFit="contain"
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ProductDetailsModal;
